/**
 * identity — ЕДИНСТВЕННОЕ место, где сайт достаёт личность пользователя.
 *
 * Зачем отдельный модуль. Раньше id вынимали из JWT кто где: `payload.id`
 * в auth.js, ручной `atob` + `Number(...) || 0` в GdMenuPage. Пока id —
 * число, это работает. Но v2 кладёт в токен UUID-строку в поле `sub`,
 * и тогда `Number("018f...")` даёт NaN, а `|| 0` превращает его в ноль —
 * то есть в другого пользователя. Молча, без ошибки в консоли.
 *
 * Поэтому правило: остальной код НЕ разбирает токен сам, а зовёт
 * currentUserId(). Формат токена можно менять, не трогая страницы.
 *
 * Поддерживаются оба формата сразу — это нужно на время перехода, пока
 * SSO-мост выдаёт v1-токен, а identity v2 уже отдаёт UUID:
 *   v1: {"id": 1234}                 → 1234 (число)
 *   v2: {"sub": "018f-...-a1b2"}     → "018f-...-a1b2" (строка)
 *
 * ВАЖНО: возвращаем id КАК ЕСТЬ, без приведения к числу. Сравнивать
 * идентификаторы нужно через sameUser(), а не через ===.
 */
// Намеренно НЕ импортируем client.js: этот модуль про содержимое токена,
// а не про HTTP. Без зависимости он тестируется в отрыве от сети и не
// создаёт цикл импортов, если клиенту сам понадобится знать личность.
const TOKEN_KEY = "rublox_token";

function readToken() {
  try {
    return localStorage.getItem(TOKEN_KEY) || null;
  } catch {
    return null;
  }
}

/** Разобрать payload JWT. null — если токена нет или он битый. */
export function decodeTokenPayload(token) {
  const t = token ?? readToken();
  if (!t) return null;
  try {
    const part = t.split(".")[1];
    if (!part) return null;
    const b64 = part.replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "===".slice((b64.length + 3) % 4);
    return JSON.parse(decodeURIComponent(escape(atob(padded))));
  } catch {
    return null;
  }
}

/**
 * Идентификатор текущего пользователя или null.
 *
 * Порядок полей не случаен: `sub` первым, потому что это стандарт JWT
 * и формат v2, к которому мы идём. `id`/`user_id` — наследие v1.
 */
export function currentUserId(token) {
  const p = decodeTokenPayload(token);
  if (!p) return null;
  const raw = p.sub ?? p.id ?? p.user_id ?? p.userId ?? null;
  if (raw === null || raw === undefined || raw === "") return null;
  return raw;
}

/**
 * Гость ли это. У гостевых токенов v2 в `sub` лежит "guest_<что-то>".
 */
export function isGuestToken(token) {
  const p = decodeTokenPayload(token);
  if (!p) return false;
  if (p.guest === true || p.type === "guest") return true;
  return typeof p.sub === "string" && p.sub.startsWith("guest_");
}

/**
 * Один ли это пользователь.
 *
 * Сравниваем строками: id может прийти числом из JWT и строкой из JSON
 * (или наоборот), а `1234 === "1234"` даёт false. Раньше это лечили
 * через Number() с обеих сторон — приём, который ломается на UUID.
 */
export function sameUser(a, b) {
  if (!isRealId(a) || !isRealId(b)) return false;
  return String(a) === String(b);
}

/**
 * Ключ для Set/Map по id — всегда строка, либо null для мусора.
 * Нужен там, где раньше складывали `Number(id)` в Set: на UUID это
 * давало множество из NaN, и проверки `.has()` врали.
 */
export function idKey(v) {
  return isRealId(v) ? String(v) : null;
}

/**
 * Похоже ли значение на настоящий id.
 *
 * Ноль и "0" отбрасываем намеренно: именно в них превращался UUID при
 * старом `Number(id) || 0`. Если такие «нули» просочатся, sameUser(0, 0)
 * скажет «это один и тот же человек» — то есть покажет одному
 * пользователю данные другого. Лучше вернуть false и не показать ничего.
 */
function isRealId(v) {
  if (v === null || v === undefined || v === "") return false;
  const s = String(v);
  return s !== "0" && s !== "NaN" && s !== "undefined" && s !== "null";
}

/**
 * Числовой id — ТОЛЬКО для старых v1-эндпоинтов, которые физически
 * не принимают UUID (например `/users/{id}/...` с числом в пути).
 * Возвращает null, если id не число: вызывающий код должен это учесть,
 * а не получить молчаливый 0.
 */
export function currentNumericUserId(token) {
  const id = currentUserId(token);
  if (id === null) return null;
  const n = Number(id);
  return Number.isFinite(n) && n > 0 ? n : null;
}
